import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { equalInLC, FCX } from "@datassential/platform-ui-lib";
import { useFilters, useMappedFiltersToClient } from "@vms/SelectFilters/SelectFiltersContext";
import { useGetByo } from "@apiHooks/useGetByo";
import useQueryState from "@hooks/useQueryState";
import { parseWordsFromString } from "@core/helpers";
import { components } from "@api/api";
import {
    BYO_INSTANT_CHART_ID_QUERY_PARAM_NAME,
    BYO_SETTINGS_ID_QUERY_PARAM_NAME, BYO_WORDS_COUNT_QUERY_PARAM_NAME,
    BYO_WORDS_QUERY_PARAM_NAME
} from "@constants/general";
import useByoSetting, { ByoSettingOutput } from "@apiHooks/useByoSetting";
import { useInstantChart } from "@apiHooks/useInstantChart";
import { SlicesEnum } from "@models/ByoTableData";

export enum ByoAnalyticType {
    Penetration = 1,
    Incidence = 2,
    Price = 3,
}

export enum ByoValueType {
    Pct = 1,
    Count = 2,
}

type API__ByoWord = components["schemas"]["Application.Byo.Models.ByoViewModel"];

export type ByoWord = API__ByoWord & {
    keyword: NonNullable<API__ByoWord['keyword']>;
    isLoading?: boolean;
};

export enum WordExtensionType {
    Include = 1,
    Exclude = 2,
    PeerInclude = 3,
}

type ByoContextValue = {
    data: ByoWord[];
    addWordsFromQuery: (query: string) => ByoWord[];
    updateKeyword: (name: string, updates: Partial<ByoWord['keyword']>) => void;
    removeItem: (name: string) => void;

    byoSettings?: ByoSettingOutput | null;
    refetchByoSettings?: () => void;

    activeSliceId: SlicesEnum;
    setActiveSliceId: Dispatch<SetStateAction<SlicesEnum>>;
    activeValueType: ByoValueType;
    setActiveValueType: Dispatch<SetStateAction<ByoValueType>>;
    activeAnalyticsType: ByoAnalyticType;
    setActiveAnalyticsType: Dispatch<SetStateAction<ByoAnalyticType>>;
}

const DEFAULT_VALUE: ByoContextValue = {
    data: [],
    addWordsFromQuery: () => [],
    updateKeyword: () => null,
    removeItem: () => null,
    activeSliceId: SlicesEnum.Segments,
    setActiveSliceId: () => null,
    activeValueType: ByoValueType.Pct,
    setActiveValueType: () => null,
    activeAnalyticsType: ByoAnalyticType.Penetration,
    setActiveAnalyticsType: () => null,
};

const ByoDataContext = createContext<ByoContextValue>(DEFAULT_VALUE);

export function useByoDataContext() {
    return useContext(ByoDataContext);
}

export const ByoDataContextProvider: FCX = ({
    children,
}) => {
    const { keywords, filters, setPartial } = useFilters();
    const mapFiltersToClient = useMappedFiltersToClient();
    const [wordsCountString] = useQueryState(BYO_WORDS_COUNT_QUERY_PARAM_NAME, '0');
    const wordsCount = Number(wordsCountString);
    const [data, setData] = useState<ByoWord[]>(Array(wordsCount).fill(0).map(() => ({
        isLoading: true,
        keyword: {
            id: 0,
            name: '',
            wordExtensions: [],
        },
    })));

    const [wordsString] = useQueryState(BYO_WORDS_QUERY_PARAM_NAME, '');
    const [isWordsStringResolved, setIsWordsStringResolved] = useState(wordsString === '');

    const [byoSettingsIdString] = useQueryState(BYO_SETTINGS_ID_QUERY_PARAM_NAME);
    const byoSettingsId: number | null = byoSettingsIdString ? Number(byoSettingsIdString) : null;
    const [isByoSettingsIdResolved, setIsByoSettingsIdResolved] = useState(byoSettingsId === null);
    const { data: byoSettingsData, doFetch: refetchByoSettings } = useByoSetting(byoSettingsId);

    const [instantChartIdString] = useQueryState(BYO_INSTANT_CHART_ID_QUERY_PARAM_NAME);
    const instantChartId: number | null = instantChartIdString ? Number(instantChartIdString) : null;
    const [isInstantChartIdResolved, setIsInstantChartIdResolved] = useState(instantChartId === null);
    const { data: instantChartData } = useInstantChart(instantChartId);

    const [activeSliceId, setActiveSliceId] = useState<SlicesEnum>(DEFAULT_VALUE.activeSliceId);
    const [activeValueType, setActiveValueType] = useState<ByoValueType>(DEFAULT_VALUE.activeValueType);
    const [activeAnalyticsType, setActiveAnalyticsType] = useState<ByoAnalyticType>(DEFAULT_VALUE.activeAnalyticsType);

    const { doFetch } = useGetByo();

    const fetchWordsData = useCallback(
        async (newWords: ByoWord[]) => {
            const keywords = newWords.map(i => i.keyword);
            const response = await doFetch(keywords);

            if (response) {
                setData(prev => prev.map(i => {
                    const updatedWord = response.data.find(
                        r => equalInLC(
                            r.keyword?.name ?? '',
                            i.keyword?.name ?? ''
                        )
                    );
                    if (updatedWord) {
                        return {
                            ...i,
                            ...updatedWord,
                            keyword: updatedWord.keyword ?? i.keyword,
                            pairedFlavors: updatedWord.pairedFlavors ?? i.pairedFlavors,
                            isLoading: false,
                        };
                    }
                    return i;
                }));
            }
        },
        [doFetch]
    );

    const addWordsFromQuery = useCallback(
        (query: string): ByoWord[] => {
            const result: ByoWord[] = [];

            setData(prev => {
                const wordsNames = parseWordsFromString(query).filter(i => !prev.some(
                    w => equalInLC(w.keyword?.name ?? '', i.trim()))
                );

                wordsNames.forEach((wordName) => {
                    const keyword = keywords.find(k => {
                        return equalInLC(k.name ?? '', wordName)
                            || k.wordExtensions?.some(
                                e => e.wordType === WordExtensionType.PeerInclude
                                    && equalInLC(e.name ?? '', wordName)
                            );
                    });

                    result.push({
                        keyword: keyword ?? {
                            id: 0,
                            name: wordName.trim().toUpperCase(),
                            wordExtensions: [],
                        },
                        pairedFlavors: [],
                        isLoading: true,
                    });
                });

                return [...prev, ...result];
            });

            if (result.length > 0) {
                fetchWordsData(result);
            }

            return result;
        },
        [keywords, fetchWordsData]
    );

    useEffect(() => {
        addWordsFromQuery(wordsString);
        setTimeout(() => {
            setIsWordsStringResolved(true);
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wordsString]);

    useEffect(() => {
        if (byoSettingsData) {
            const newData = (byoSettingsData.words ?? []).map(
                i => ({
                    isLoading: true,
                    keyword: i,
                    pairedFlavors: [],
                })
            );
            setPartial(mapFiltersToClient(byoSettingsData.filters))
            if (newData.length) {
                setData(newData);
                fetchWordsData(newData);
            }
            setTimeout(
                () => {
                    setIsByoSettingsIdResolved(true);
                },
                100,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [byoSettingsData]);

    useEffect(() => {
        if (instantChartData) {
            setPartial(mapFiltersToClient(instantChartData?.filter?.filters))
            setData((instantChartData.result ?? []).map(i => ({
                ...i,
                keyword: i.keyword ?? {},
                pairedFlavors: i.pairedFlavors ?? [],
                isLoading: false,
            })));
            setTimeout(
                () => {
                    setIsInstantChartIdResolved(true)
                },
                100,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instantChartData]);

    const isAllResolved = useMemo(
        () => {
            let result = true;

            result = result && isWordsStringResolved;
            if (byoSettingsId !== null) {
                result = result && isByoSettingsIdResolved;
            }
            if (instantChartId !== null) {
                result = result && isInstantChartIdResolved;
            }

            return result;
        },
        [
            isWordsStringResolved,
            isByoSettingsIdResolved,
            isInstantChartIdResolved,
            byoSettingsId,
            instantChartId,
        ]
    );

    const reFetchAll = useCallback(
        async () => {
            if (isAllResolved) {
                setData(prev => prev.map(i => ({
                    keyword: i.keyword,
                    pairedFlavors: i.pairedFlavors ?? [],
                    isLoading: true,
                })));
                fetchWordsData(data);
            }
        },
        [data, fetchWordsData, isAllResolved]
    );

    useEffect(() => {
        reFetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    const updateKeyword = useCallback(
        (name: string, updates: Partial<ByoWord['keyword']>) => {
            setData((prev) => {
                const wordIndex = prev.findIndex((item) => equalInLC(item.keyword.name ?? '', name));
                const word = {
                    ...prev[wordIndex],
                    isLoading: true,
                    keyword: {
                        ...prev[wordIndex].keyword,
                        ...updates,
                    },
                };

                fetchWordsData([word]);

                return [
                    ...prev.slice(0, wordIndex),
                    word,
                    ...prev.slice(wordIndex + 1),
                ];
            });
        },
        [fetchWordsData]
    );

    const removeItem = useCallback((name: string) => {
        setData((prev) => prev.filter((item) => !equalInLC(item.keyword.name ?? '', name)));
    }, []);

    const value = useMemo(
        (): ByoContextValue => ({
            ...DEFAULT_VALUE, // temporary to avoid adding new fields
            data,
            addWordsFromQuery,
            updateKeyword,
            removeItem,
            byoSettings: byoSettingsData,
            refetchByoSettings,
            activeSliceId,
            setActiveSliceId,
            activeValueType,
            setActiveValueType,
            activeAnalyticsType,
            setActiveAnalyticsType,
        }),
        [
            data,
            addWordsFromQuery,
            updateKeyword,
            removeItem,
            byoSettingsData,
            refetchByoSettings,
            activeSliceId,
            activeValueType,
            activeAnalyticsType,
        ]
    );

    return (
        <ByoDataContext.Provider value={value}>
            {children}
        </ByoDataContext.Provider>
    );
};